import { useState, type ReactNode } from 'react';
import { Check, Pencil } from 'lucide-react';
import {
  patchCrmLeadTask,
  patchCrmOrderTask,
  type CrmTask,
  type AdminManagerBrief,
} from '../../../api';
import { CRM_TASK_KIND_LABELS } from './crmLabels';
import { CrmTaskDueFields, mergeDueLocal, splitDueLocal } from './CrmTaskDueFields';

type Props = {
  task: CrmTask;
  entityKind: 'order' | 'lead';
  entityId: number;
  managers?: AdminManagerBrief[];
  onUpdated: () => void;
  hapticSelection: () => void;
  hapticSuccess: () => void;
};

function assigneeLabel(task: CrmTask, managers?: AdminManagerBrief[]): string | null {
  const mgr = managers?.find((m) => m.id === task.assignee_id);
  if (!mgr) return null;
  return (mgr.first_name || mgr.username || `#${mgr.id}`).trim();
}

function dueLabel(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CrmTaskRow({
  task,
  entityKind,
  entityId,
  managers,
  onUpdated,
  hapticSelection,
  hapticSuccess,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [dueAt, setDueAt] = useState(() => {
    const p = splitDueLocal(task.due_at);
    return mergeDueLocal(p.date, p.time);
  });
  const [saving, setSaving] = useState(false);
  const isOpen = task.status === 'open';
  const assignee = assigneeLabel(task, managers);
  const kindLabel = CRM_TASK_KIND_LABELS[task.kind] || task.kind;
  const overdue = isOpen && task.is_overdue;

  const patch = async (payload: { status?: string; title?: string; due_at?: string }) => {
    if (entityKind === 'order') await patchCrmOrderTask(entityId, task.id, payload);
    else await patchCrmLeadTask(entityId, task.id, payload);
  };

  const saveEdit = async () => {
    if (!title.trim() || !dueAt) return;
    hapticSelection();
    setSaving(true);
    try {
      await patch({ title: title.trim(), due_at: dueAt });
      hapticSuccess();
      setEditing(false);
      onUpdated();
    } finally {
      setSaving(false);
    }
  };

  const complete = async () => {
    hapticSelection();
    setSaving(true);
    try {
      await patch({ status: 'done' });
      hapticSuccess();
      onUpdated();
    } finally {
      setSaving(false);
    }
  };

  const snoozeTomorrow = async () => {
    hapticSelection();
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(18, 0, 0, 0);
    setSaving(true);
    try {
      await patch({ due_at: d.toISOString() });
      hapticSuccess();
      onUpdated();
    } finally {
      setSaving(false);
    }
  };

  return (
    <TaskShell overdue={overdue} done={!isOpen} editing={editing}>
      {!editing ? (
        <>
          <TaskBody
            task={task}
            kindLabel={kindLabel}
            assignee={assignee}
            isOpen={isOpen}
            overdue={overdue}
          />
          {isOpen && (
            <div className="flex flex-wrap gap-1.5 shrink-0 justify-end">
              <ActionBtn
                disabled={saving}
                onClick={() => {
                  hapticSelection();
                  setTitle(task.title);
                  const p = splitDueLocal(task.due_at);
                  setDueAt(mergeDueLocal(p.date, p.time));
                  setEditing(true);
                }}
                bg="rgba(99,179,237,0.15)"
                color="var(--cyan-bright)"
              >
                <Pencil size={12} className="inline mr-0.5" />
                Изменить
              </ActionBtn>
              <ActionBtn disabled={saving} onClick={() => void snoozeTomorrow()} bg="rgba(255,255,255,0.06)" color="var(--text-2)">
                +1 д
              </ActionBtn>
              <ActionBtn disabled={saving} onClick={() => void complete()} bg="rgba(72,187,120,0.2)" color="#68D391">
                <Check size={12} className="inline mr-0.5" />
                Готово
              </ActionBtn>
            </div>
          )}
        </>
      ) : (
        <TaskEditForm
          title={title}
          dueAt={dueAt}
          saving={saving}
          onTitle={setTitle}
          onDueAt={setDueAt}
          onCancel={() => setEditing(false)}
          onSave={() => void saveEdit()}
        />
      )}
    </TaskShell>
  );
}

function TaskShell({
  overdue,
  done,
  editing,
  children,
}: {
  overdue: boolean;
  done: boolean;
  editing: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`p-3 rounded-xl flex ${editing ? 'flex-col' : 'justify-between'} gap-2`}
      style={{
        background: overdue ? 'rgba(252,129,74,0.1)' : done ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${overdue ? 'rgba(252,129,74,0.35)' : 'var(--border-subtle)'}`,
        opacity: done ? 0.72 : 1,
      }}
    >
      {children}
    </div>
  );
}

function ActionBtn({
  children,
  disabled,
  onClick,
  bg,
  color,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
  bg: string;
  color: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="press text-[10px] font-bold px-2 py-1 rounded-lg touch-manipulation min-h-[36px] disabled:opacity-50"
      style={{ background: bg, color }}
    >
      {children}
    </button>
  );
}

function TaskBody({
  task,
  kindLabel,
  assignee,
  isOpen,
  overdue,
}: {
  task: CrmTask;
  kindLabel: string;
  assignee: string | null;
  isOpen: boolean;
  overdue: boolean;
}) {
  return (
    <div className="min-w-0 flex-1">
      <p className={`text-xs font-semibold truncate ${isOpen ? '' : 'line-through'}`}>{task.title}</p>
      <p className="text-[10px]" style={{ color: overdue ? '#FC814A' : 'var(--text-3)' }}>
        {kindLabel}
        {assignee ? ` · ${assignee}` : ''}
        {' · '}
        {isOpen ? 'до ' : ''}
        {task.due_at ? dueLabel(task.due_at) : '—'}
        {!isOpen ? ' · выполнено' : ''}
      </p>
    </div>
  );
}

function TaskEditForm({
  title,
  dueAt,
  saving,
  onTitle,
  onDueAt,
  onCancel,
  onSave,
}: {
  title: string;
  dueAt: string;
  saving: boolean;
  onTitle: (v: string) => void;
  onDueAt: (v: string) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <div className="space-y-2 w-full">
      <input
        className="input-glass w-full text-xs min-h-[40px] touch-manipulation"
        value={title}
        onChange={(e) => onTitle(e.target.value)}
        placeholder="Что сделать"
      />
      <CrmTaskDueFields
        value={dueAt}
        onChange={onDueAt}
        disabled={saving}
        dateClassName="input-glass flex-1 text-xs min-h-[40px] touch-manipulation"
        timeClassName="input-glass w-[5.5rem] shrink-0 text-xs min-h-[40px] touch-manipulation text-center"
      />
      <div className="flex gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={onCancel}
          className="press flex-1 py-2 rounded-lg text-[10px] font-bold disabled:opacity-50"
          style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}
        >
          Отмена
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={onSave}
          className="press flex-1 py-2 rounded-lg text-[10px] font-bold disabled:opacity-50"
          style={{ background: 'rgba(72,187,120,0.2)', color: '#68D391' }}
        >
          {saving ? '…' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}
